import { useQuery } from "@tanstack/react-query";
import type { FeatureCollection, Point } from "geojson";
import { type ComponentType, useMemo } from "react";

import { api } from "../../api/client";
import { t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { revealPaint, revealStyle, useMapRevealed } from "../reveal";
import { FRIEND_BLUE } from "../units/symbols";
import { useDeployment } from "../units/useDeployment";

/** Calque « ZEF et ZEA » (demande d'Alexis du 14/10/2026, exception à la carte figée) : zones d'effort des feux de
 *  l'annexe A de l'OPO de division et zone d'engagement ALAT de l'aérocombat, les mêmes que la page A4 de l'annexe.
 *  Le serveur les recalcule sur l'état courant du dossier : la clé porte la version du dossier, une unité déplacée,
 *  une LIMA glissée ou un OPO régénéré (`["fire-zones", id]` invalidé) les relit. ZEF en bleu ami, ZEA en vert,
 *  comme la pièce modèle d'Alexis. */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
  Marker: ComponentType<any>;
}

export const ZEA_GREEN = "#2E7D32";

interface ZoneProps {
  kind: "zef" | "zea";
  number: number;
  label: string;
  phase: number | null;
  window: string;
  role: "area" | "label";
}

export function useFireZones(enabled = true) {
  const exerciseId = useUi((s) => s.exerciseId);
  const { data: deployment } = useDeployment();
  const division = deployment?.level === "DIVISION";
  const version = deployment?.exercise_version ?? null;
  return useQuery({
    queryKey: ["fire-zones", exerciseId, version],
    queryFn: () => api.fireZones(exerciseId as string),
    enabled: enabled && division && Boolean(exerciseId),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/** Nombre de zones du calque (ZEF et ZEA). */
export function fireZoneCount(data: FeatureCollection | undefined): number {
  return (data?.features ?? []).filter((f) => (f.properties as ZoneProps | null)?.role === "area").length;
}

export function FireZonesLayers({ Source, Layer, Marker }: EngineParts) {
  const visible = useUi((s) => s.controlVisible.fireZones !== false);
  const revealed = useMapRevealed();
  const { data } = useFireZones(visible);
  const areas = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: (data?.features ?? []).filter((f) => (f.properties as ZoneProps | null)?.role === "area"),
    }),
    [data],
  );
  const labels = useMemo(
    () =>
      (data?.features ?? []).filter((f) => (f.properties as ZoneProps | null)?.role === "label") as unknown as {
        geometry: Point;
        properties: ZoneProps;
      }[],
    [data],
  );
  if (!visible || !data || !areas.features.length) return null;
  const colour = ["case", ["==", ["get", "kind"], "zea"], ZEA_GREEN, FRIEND_BLUE];
  return (
    <>
      <Source id="gx-fire-zones" type="geojson" data={areas}>
        {/* ZEA dessous, ZEF dessus : une ZEF dans la ZEA reste lisible. */}
        <Layer
          id="fire-zones-fill"
          type="fill"
          paint={revealPaint("fill", { "fill-color": colour, "fill-opacity": ["case", ["==", ["get", "kind"], "zea"], 0.1, 0.14] }, revealed)}
        />
        <Layer
          id="fire-zones-outline"
          type="line"
          layout={{ "line-join": "round" }}
          paint={revealPaint("line", { "line-color": colour, "line-width": ["case", ["==", ["get", "kind"], "zea"], 2.5, 2] }, revealed)}
        />
      </Source>
      {labels.map((label) => (
        <Marker
          key={`${label.properties.kind}-${label.properties.number}`}
          longitude={label.geometry.coordinates[0]}
          latitude={label.geometry.coordinates[1]}
          anchor="center"
          style={{ pointerEvents: "none" }}
        >
          <div style={revealStyle(revealed)}>
            <div
              title={
                label.properties.kind === "zea"
                  ? t("Zone d'engagement ALAT · {window}", { window: label.properties.window })
                  : t("Zone d'effort des feux · T{n} · {window}", { n: label.properties.phase ?? "", window: label.properties.window })
              }
              className="pointer-events-none flex h-5 items-center rounded-md border border-line-strong bg-surface px-1.5 font-sans shadow-[0_1px_2px_rgb(16_18_24/0.12)]"
            >
              <span
                className="font-mono text-[12px] leading-none font-bold"
                style={{ color: label.properties.kind === "zea" ? ZEA_GREEN : FRIEND_BLUE }}
              >
                {label.properties.label}
              </span>
            </div>
          </div>
        </Marker>
      ))}
    </>
  );
}
